/* eslint-disable no-prototype-builtins */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/** For category detials page, we need to display the product of the category and its sub
 * categories and in order to make this via authoring we need to makethe PLP page as a widget
 */

import { xCommonUpdate } from "@eisinc/App/Handlers/XNG/Common";
import { loadingOverflow } from "@eisinc/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { CategoryDetailsPageContext } from "@eisinc/Overrides/Pages/CategoryDetailsPage";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearProducts";
import displayProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import ProductCompareFlyOut from "@insite/content-library/Components/ProductCompareFlyOut";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const path =
        selectedCategoryPath || (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const {
        pages: {
            productList: { productFilters, parameter, isSearchPage, filterQuery, productInfosByProductId },
        },
    } = state;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const productsDataView = getProductsDataView(state, parameter);
    const categoryProperties = catalogPage?.properties;
    return {
        stockedItemsOnly: productFilters.stockedItemsOnly,
        query: productFilters.query,
        productsDataView,
        firstProductDetailPath: productsDataView.value
            ? productInfosByProductId[productsDataView.value[0]?.id]?.productDetailPath
            : undefined,
        filterQuery,
        isSearchPage,
        search: location.search,
        path,
        breadcrumbLinks: state.components.breadcrumbs.links,
        productListCatalogPage: getCatalogPageStateByPath(state, path).value,
        websiteName: state.context.website.name,
        pages: state.pages,
        location: getLocation(state),
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        categoryProperties,
        lastCategoryPathRequested: state.xCommon.lastCategoryPathRequested || "",
    };
};

const mapDispatchToProps = {
    displayProducts,
    setBreadcrumbs,
    clearProducts,
    xCommonUpdate,
};

export const ProductListPageDataContext = React.createContext<{ ref?: React.RefObject<HTMLSpanElement | null> }>({
    ref: undefined,
});

type Props = HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    WidgetProps;

class ProductListPage extends React.Component<Props> {
    afterFilters = React.createRef<HTMLSpanElement>();

    loadProducts() {
        if (typeof localStorage === "undefined") {
            return;
        }

        const { displayProducts, path, search } = this.props;

        let isSearchListActive = localStorage?.getItem("isSearchListActive");
        if (isSearchListActive) {
            isSearchListActive = JSON.parse(isSearchListActive);
        }

        if (
            (this.props.categoryProperties?.hasOwnProperty("displayCategoryProducts") &&
                !this.props.categoryProperties["displayCategoryProducts"]) ||
            !this.props.categoryProperties?.hasOwnProperty("displayCategoryProducts")
        ) {
            return;
        }

        displayProducts({
            path,
            queryString: search,
            isSearch: this.isSearchPath(),
        } as any);
    }

    isSearchPath() {
        return !!this.props.path.match(/^\/search\b/i);
    }

    UNSAFE_componentWillMount(): void {
        const { productsDataView, lastCategoryPathRequested, category } = this.props;

        const forceProductsLoad =
            productsDataView.value && !productsDataView.isLoading && lastCategoryPathRequested !== category?.path;

        // xng change: EIS-3648: adding empty array check for value so it loads the product when
        // going back from no result page

        // same as on PLP page
        if (
            (!productsDataView.isLoading &&
                (!productsDataView.value || productsDataView.value.length === 0) &&
                lastCategoryPathRequested !== category?.path) ||
            forceProductsLoad
        ) {
            this.props.xCommonUpdate({ key: "lastCategoryPathRequested", value: this.props.category?.path });
            this.loadProducts();
        }

        if (this.props.productListCatalogPage && !this.props.breadcrumbLinks) {
            this.setProductListBreadcrumbs();
        } else if (this.props.isSearchPage && !this.props.breadcrumbLinks) {
            this.setSearchBreadcrumbs();
        }
    }

    componentWillUnmount(): void {
        this.props.clearProducts();
    }

    componentDidUpdate(prevProps: Props): void {
        const {
            filterQuery,
            location: { pathname, search },
            firstProductDetailPath,
            websiteName,
            productListCatalogPage,
        } = this.props;

        // handle the query string change requests initiated by the filtering widget setQueryFilter calls
        if (filterQuery !== prevProps.filterQuery) {
            this.props.history.push(filterQuery ? `${pathname}?${filterQuery}` : pathname);
            return;
        }

        if (this.props.productsDataView.value && pathname === prevProps.location.pathname) {
            const { searchTermRedirectUrl, exactMatch, value: products } = this.props.productsDataView;
            if (searchTermRedirectUrl) {
                if (searchTermRedirectUrl.lastIndexOf("http", 0) === 0) {
                    window.location.replace(searchTermRedirectUrl);
                } else {
                    this.props.history.replace(searchTermRedirectUrl);
                }
                return;
            }

            if (exactMatch && !this.props.stockedItemsOnly) {
                let productDetailUrl = firstProductDetailPath ?? products[0].canonicalUrl;
                const parsedQuery = parseQueryString<{ query: string }>(search);
                const query = parsedQuery.query;
                if (!query) {
                    return;
                }
                if (productDetailUrl.indexOf("?") !== -1) {
                    productDetailUrl += `&criteria=${encodeURIComponent(query)}`;
                } else {
                    productDetailUrl += `?criteria=${encodeURIComponent(query)}`;
                }

                this.props.history.replace(productDetailUrl);
                return;
            }
        }

        if (search !== prevProps.search || this.props.path !== prevProps.path) {
            this.loadProducts();
        }

        if (productListCatalogPage && productListCatalogPage !== prevProps.productListCatalogPage) {
            trackPageChange();
            this.setProductListBreadcrumbs();
        }

        if (productListCatalogPage) {
            if (this.props.isSearchPage && this.props.query !== prevProps.query) {
                this.setSearchBreadcrumbs();
            }
        }
    }

    setProductListBreadcrumbs() {
        this.props.setBreadcrumbs({
            links: this.props.productListCatalogPage!.breadCrumbs!.map(o => ({ children: o.text, href: o.url })),
        });
    }

    setSearchBreadcrumbs() {
        this.props.setBreadcrumbs({ links: [{ children: `${translate("Search Results")}: ${this.props.query}` }] });
    }

    render() {
        const { productsDataView } = this.props;
        if (
            this.props.productsDataView.value &&
            this.props.productsDataView.exactMatch &&
            !this.props.stockedItemsOnly
        ) {
            // prevent a flash of data when PLP immediately redirects to PDP
            return null;
        }

        if (
            (this.props.categoryProperties?.hasOwnProperty("displayCategoryProducts") &&
                !this.props.categoryProperties["displayCategoryProducts"]) ||
            !this.props.categoryProperties?.hasOwnProperty("displayCategoryProducts")
        ) {
            return <></>;
        }

        return (
            <LoadingOverlay {...loadingOverflow} loading={productsDataView.isLoading}>
                <CategoryContext.Provider value={this.props.category}>
                    <ProductListPageDataContext.Provider value={{ ref: this.afterFilters }}>
                        <Zone contentId={this.props.id} zoneName="Content" />
                        <AddToListModal />
                        <ProductCompareFlyOut />
                    </ProductListPageDataContext.Provider>
                </CategoryContext.Provider>
            </LoadingOverlay>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(ProductListPage)),
    definition: {
        group: "Categories",
        displayName: "Partial Product List",
        allowedContexts: [CategoryDetailsPageContext],
    },
};

export default widgetModule;
